import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkle, Wrench, ArrowsClockwise, ArrowLeft, PaperPlaneTilt } from "@phosphor-icons/react";
import api from "@/lib/api";
import { APP_VERSION } from "@/version";
import { useAuth } from "@/context/AuthContext";

const TYPE_META = {
  added:   { label: "Nytt",     Icon: Sparkle,         cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  fixed:   { label: "Fix",      Icon: Wrench,          cls: "bg-amber-100 text-amber-800 border-amber-200" },
  changed: { label: "Ändrat",   Icon: ArrowsClockwise, cls: "bg-blue-100 text-blue-800 border-blue-200" },
};

function formatSwedishDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Changelog() {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.is_super_admin;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // version being sent

  useEffect(() => {
    api.get("/changelog")
      .then((r) => {
        setEntries(r.data || []);
        // Markera som läst så att pricken försvinner
        api.post("/changelog/mark-read").catch(() => {});
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Kunde inte ladda versionsloggen"))
      .finally(() => setLoading(false));
  }, []);

  const sendNewsletter = async (version, testOnly) => {
    const label = testOnly ? "TEST-utskick till dig själv" : "utskick till ALLA admins";
    if (!window.confirm(`Bekräfta ${label} för v${version}?`)) return;
    setSending(version + (testOnly ? "-test" : ""));
    try {
      const r = await api.post("/changelog/notify", { version, test_only: !!testOnly });
      toast.success(`Skickat: ${r.data.sent} lyckade / ${r.data.failed} misslyckade (${r.data.recipients_total} mottagare)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte skicka nyhetsbrev");
    } finally {
      setSending(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto py-6 md:py-10 px-4 md:px-6" data-testid="changelog-page">
      <div className="mb-6">
        <Link
          to="/settings"
          className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-3"
          data-testid="changelog-back-link"
        >
          <ArrowLeft size={14} /> Tillbaka till Inställningar
        </Link>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Versionslogg</h1>
        <p className="text-text-muted mt-2 text-sm md:text-base">
          Här ser du allt som byggs och fixas i Tidsrapporten över tid.
          Nuvarande version är <span className="font-mono font-semibold">v{APP_VERSION}</span>.
        </p>
      </div>

      {loading ? (
        <p className="text-text-muted">Laddar…</p>
      ) : entries.length === 0 ? (
        <p className="text-text-muted">Inga poster ännu.</p>
      ) : (
        <ol className="relative border-l-2 border-app pl-6 space-y-8" data-testid="changelog-list">
          {entries.map((e, i) => (
            <li key={e.version} data-testid={`changelog-entry-${e.version}`} className="relative">
              <span className={`absolute -left-[35px] top-1 w-4 h-4 rounded-full border-2 border-app ${
                i === 0 ? "bg-[var(--brand-rust)]" : "bg-white"
              }`} />
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2">
                <h2 className="text-xl font-bold font-mono">v{e.version}</h2>
                {i === 0 && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[var(--brand-rust)] text-white">
                    Senaste
                  </span>
                )}
                <span className="text-sm text-text-muted">{formatSwedishDate(e.date)}</span>
              </div>
              {e.title && <p className="font-medium mb-3 text-text-primary">{e.title}</p>}
              <ul className="space-y-2">
                {(e.changes || []).map((c, idx) => {
                  const meta = TYPE_META[c.type] || TYPE_META.changed;
                  const Icon = meta.Icon;
                  return (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <span className={`inline-flex items-center gap-1 shrink-0 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${meta.cls}`}>
                        <Icon size={10} weight="fill" /> {meta.label}
                      </span>
                      <span className="text-text-primary">{c.text}</span>
                    </li>
                  );
                })}
              </ul>
              {isSuperAdmin && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid={`changelog-notify-actions-${e.version}`}>
                  <button
                    type="button"
                    data-testid={`changelog-notify-test-${e.version}`}
                    disabled={sending === `${e.version}-test`}
                    onClick={() => sendNewsletter(e.version, true)}
                    className="tk-btn-outline !py-1 !px-3 text-xs inline-flex items-center gap-1"
                  >
                    <PaperPlaneTilt size={12} /> {sending === `${e.version}-test` ? "Skickar…" : "Testa till mig"}
                  </button>
                  <button
                    type="button"
                    data-testid={`changelog-notify-all-${e.version}`}
                    disabled={sending === e.version}
                    onClick={() => sendNewsletter(e.version, false)}
                    className="tk-btn-primary !py-1 !px-3 text-xs inline-flex items-center gap-1"
                  >
                    <PaperPlaneTilt size={12} weight="fill" /> {sending === e.version ? "Skickar…" : "Skicka till alla admins"}
                  </button>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      <div className="mt-10 pt-6 border-t border-app text-xs text-text-muted">
        Har du önskemål eller frågor? Använd <Link to="/settings" className="underline">Hjälp & Feedback</Link> i inställningarna.
      </div>
    </div>
  );
}
